/**
 * Education Management Service
 * Handles all education-related operations including degrees, certifications,
 * courses, and educational achievements tracking
 */

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    multiGet: async (keys) => keys.map(key => [key, null]),
    removeItem: async (key) => {}
  };
}

class EducationManagementService {
  constructor() {
    this.isInitialized = false;
    this.educationData = {
      degrees: [],
      certifications: [],
      courses: [],
      achievements: [],
      institutions: []
    };
    this.subscribers = new Map();
    this.offlineQueue = [];
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🚀 Initializing Education Management Service...');
      
      // Load cached education data
      await this.loadCachedData();
      
      // Initialize institutions database
      await this.initializeInstitutions();
      
      this.isInitialized = true;
      console.log('✅ Education Management Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Education Management Service:', error);
      return false;
    }
  }

  // Load cached education data
  async loadCachedData() {
    try {
      const keys = [
        'user_degrees',
        'user_certifications',
        'user_courses',
        'user_achievements'
      ];
      
      const cachedData = await AsyncStorage.multiGet(keys);
      
      cachedData.forEach(([key, value]) => {
        if (value) {
          const dataKey = key.replace('user_', '');
          this.educationData[dataKey] = JSON.parse(value);
        }
      });
      
      console.log('📦 Education cached data loaded');
    } catch (error) {
      console.error('❌ Error loading cached education data:', error);
    }
  }

  // Initialize institutions database
  async initializeInstitutions() {
    try {
      const institutions = [
        {
          id: 'inst_1',
          name: 'University of Oxford',
          type: 'university',
          country: 'United Kingdom',
          ranking: 1,
          verified: true
        },
        {
          id: 'inst_2',
          name: 'Cambridge University',
          type: 'university',
          country: 'United Kingdom',
          ranking: 2,
          verified: true
        },
        {
          id: 'inst_3',
          name: 'Imperial College London',
          type: 'university',
          country: 'United Kingdom',
          ranking: 3,
          verified: true
        },
        {
          id: 'inst_4',
          name: 'Coursera',
          type: 'online_platform',
          country: 'Global',
          verified: true
        },
        {
          id: 'inst_5',
          name: 'edX',
          type: 'online_platform',
          country: 'Global',
          verified: true
        }
      ];
      
      this.educationData.institutions = institutions;
      await AsyncStorage.setItem('institutions', JSON.stringify(institutions));
      
    } catch (error) {
      console.error('❌ Error initializing institutions:', error);
    }
  }

  // Get user education data
  async getUserEducation(userId) {
    try {
      console.log('🎓 Getting user education for:', userId);
      
      return {
        success: true,
        data: {
          degrees: this.educationData.degrees,
          certifications: this.educationData.certifications,
          courses: this.educationData.courses,
          achievements: this.educationData.achievements
        }
      };
      
    } catch (error) {
      console.error('❌ Error getting user education:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add degree
  async addDegree(userId, degreeData) {
    try {
      console.log('🎓 Adding degree for:', userId);
      
      const newDegree = {
        id: `degree_${Date.now()}`,
        ...degreeData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        verified: false,
        gpaScale: degreeData.gpaScale || '4.0'
      };
      
      this.educationData.degrees.push(newDegree);
      await this.saveDegrees();
      
      // Notify subscribers
      this.notifySubscribers('degree_added', newDegree);
      
      return {
        success: true,
        data: newDegree
      };
      
    } catch (error) {
      console.error('❌ Error adding degree:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update degree
  async updateDegree(userId, degreeId, updates) {
    try {
      console.log('📝 Updating degree:', degreeId);
      
      const degreeIndex = this.educationData.degrees.findIndex(degree => degree.id === degreeId);
      
      if (degreeIndex === -1) {
        throw new Error('Degree not found');
      }
      
      this.educationData.degrees[degreeIndex] = {
        ...this.educationData.degrees[degreeIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await this.saveDegrees();
      
      // Notify subscribers
      this.notifySubscribers('degree_updated', this.educationData.degrees[degreeIndex]);
      
      return {
        success: true,
        data: this.educationData.degrees[degreeIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating degree:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete degree
  async deleteDegree(userId, degreeId) {
    try {
      console.log('🗑️ Deleting degree:', degreeId);
      
      const degreeIndex = this.educationData.degrees.findIndex(degree => degree.id === degreeId);
      
      if (degreeIndex === -1) {
        throw new Error('Degree not found');
      }
      
      const deletedDegree = this.educationData.degrees.splice(degreeIndex, 1)[0];
      await this.saveDegrees();
      
      // Notify subscribers
      this.notifySubscribers('degree_deleted', { degreeId, degree: deletedDegree });
      
      return {
        success: true,
        data: { degreeId }
      };
      
    } catch (error) {
      console.error('❌ Error deleting degree:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add certification
  async addCertification(userId, certificationData) {
    try {
      console.log('📜 Adding certification for:', userId);
      
      const newCertification = {
        id: `cert_${Date.now()}`,
        ...certificationData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        verified: false,
        credentialId: certificationData.credentialId || null,
        credentialUrl: certificationData.credentialUrl || null
      };
      
      this.educationData.certifications.push(newCertification);
      await this.saveCertifications();
      
      // Notify subscribers
      this.notifySubscribers('certification_added', newCertification);
      
      return {
        success: true,
        data: newCertification
      };
      
    } catch (error) {
      console.error('❌ Error adding certification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update certification
  async updateCertification(userId, certificationId, updates) {
    try {
      console.log('📝 Updating certification:', certificationId);
      
      const certIndex = this.educationData.certifications.findIndex(cert => cert.id === certificationId);
      
      if (certIndex === -1) {
        throw new Error('Certification not found');
      }
      
      this.educationData.certifications[certIndex] = {
        ...this.educationData.certifications[certIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await this.saveCertifications();
      
      // Notify subscribers
      this.notifySubscribers('certification_updated', this.educationData.certifications[certIndex]);
      
      return {
        success: true,
        data: this.educationData.certifications[certIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating certification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete certification
  async deleteCertification(userId, certificationId) {
    try {
      console.log('🗑️ Deleting certification:', certificationId);
      
      const certIndex = this.educationData.certifications.findIndex(cert => cert.id === certificationId);
      
      if (certIndex === -1) {
        throw new Error('Certification not found');
      }
      
      const deletedCertification = this.educationData.certifications.splice(certIndex, 1)[0];
      await this.saveCertifications();
      
      // Notify subscribers
      this.notifySubscribers('certification_deleted', { certificationId, certification: deletedCertification });
      
      return {
        success: true,
        data: { certificationId }
      };
      
    } catch (error) {
      console.error('❌ Error deleting certification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add course
  async addCourse(userId, courseData) {
    try {
      console.log('📚 Adding course for:', userId);
      
      const newCourse = {
        id: `course_${Date.now()}`,
        ...courseData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        progress: 0,
        status: 'not_started'
      };
      
      this.educationData.courses.push(newCourse);
      await this.saveCourses();
      
      // Notify subscribers
      this.notifySubscribers('course_added', newCourse);
      
      return {
        success: true,
        data: newCourse
      };
      
    } catch (error) {
      console.error('❌ Error adding course:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update course progress
  async updateCourseProgress(userId, courseId, progress, status) {
    try {
      console.log('📈 Updating course progress:', courseId);
      
      const courseIndex = this.educationData.courses.findIndex(course => course.id === courseId);
      
      if (courseIndex === -1) {
        throw new Error('Course not found');
      }
      
      this.educationData.courses[courseIndex] = {
        ...this.educationData.courses[courseIndex],
        progress,
        status,
        lastUpdated: new Date().toISOString()
      };
      
      // If course is completed, add achievement
      if (status === 'completed' && progress >= 100) {
        await this.addAchievement(userId, {
          type: 'course_completion',
          title: `Completed ${this.educationData.courses[courseIndex].title}`,
          description: `Successfully completed the course: ${this.educationData.courses[courseIndex].title}`,
          courseId,
          earnedDate: new Date().toISOString()
        });
      }
      
      await this.saveCourses();
      
      // Notify subscribers
      this.notifySubscribers('course_progress_updated', this.educationData.courses[courseIndex]);
      
      return {
        success: true,
        data: this.educationData.courses[courseIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating course progress:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add achievement
  async addAchievement(userId, achievementData) {
    try {
      console.log('🏆 Adding achievement for:', userId);
      
      const newAchievement = {
        id: `achievement_${Date.now()}`,
        ...achievementData,
        userId,
        dateAdded: new Date().toISOString()
      };
      
      this.educationData.achievements.push(newAchievement);
      await this.saveAchievements();
      
      // Notify subscribers
      this.notifySubscribers('achievement_earned', newAchievement);
      
      return {
        success: true,
        data: newAchievement
      };
      
    } catch (error) {
      console.error('❌ Error adding achievement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get education statistics
  getEducationStatistics() {
    const stats = {
      totalDegrees: this.educationData.degrees.length,
      totalCertifications: this.educationData.certifications.length,
      totalCourses: this.educationData.courses.length,
      completedCourses: this.educationData.courses.filter(course => course.status === 'completed').length,
      inProgressCourses: this.educationData.courses.filter(course => course.status === 'in_progress').length,
      totalAchievements: this.educationData.achievements.length,
      verifiedCredentials: [
        ...this.educationData.degrees.filter(degree => degree.verified),
        ...this.educationData.certifications.filter(cert => cert.verified)
      ].length
    };
    
    // Calculate average course progress
    const coursesWithProgress = this.educationData.courses.filter(course => course.progress > 0);
    stats.averageCourseProgress = coursesWithProgress.length > 0
      ? Math.round(coursesWithProgress.reduce((sum, course) => sum + course.progress, 0) / coursesWithProgress.length)
      : 0;
    
    return stats;
  }

  // Search education records
  searchEducation(query) {
    const lowercaseQuery = query.toLowerCase();
    
    return {
      degrees: this.educationData.degrees.filter(degree =>
        degree.degree.toLowerCase().includes(lowercaseQuery) ||
        degree.institution.toLowerCase().includes(lowercaseQuery) ||
        degree.fieldOfStudy.toLowerCase().includes(lowercaseQuery)
      ),
      certifications: this.educationData.certifications.filter(cert =>
        cert.name.toLowerCase().includes(lowercaseQuery) ||
        cert.issuer.toLowerCase().includes(lowercaseQuery)
      ),
      courses: this.educationData.courses.filter(course =>
        course.title.toLowerCase().includes(lowercaseQuery) ||
        course.provider.toLowerCase().includes(lowercaseQuery)
      )
    };
  }

  // Get institutions
  getInstitutions() {
    return this.educationData.institutions;
  }

  // Search institutions
  searchInstitutions(query) {
    const lowercaseQuery = query.toLowerCase();
    return this.educationData.institutions.filter(institution =>
      institution.name.toLowerCase().includes(lowercaseQuery) ||
      institution.country.toLowerCase().includes(lowercaseQuery)
    );
  }

  // Validate degree data
  validateDegreeData(degreeData) {
    const errors = {};
    
    if (!degreeData.degree || degreeData.degree.trim() === '') {
      errors.degree = 'Degree is required';
    }
    
    if (!degreeData.institution || degreeData.institution.trim() === '') {
      errors.institution = 'Institution is required';
    }
    
    if (!degreeData.fieldOfStudy || degreeData.fieldOfStudy.trim() === '') {
      errors.fieldOfStudy = 'Field of study is required';
    }
    
    if (!degreeData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (degreeData.endDate && new Date(degreeData.startDate) >= new Date(degreeData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }
    
    if (degreeData.gpa && (isNaN(degreeData.gpa) || degreeData.gpa < 0)) {
      errors.gpa = 'GPA must be a valid number';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validate certification data
  validateCertificationData(certificationData) {
    const errors = {};
    
    if (!certificationData.name || certificationData.name.trim() === '') {
      errors.name = 'Certification name is required';
    }
    
    if (!certificationData.issuer || certificationData.issuer.trim() === '') {
      errors.issuer = 'Issuer is required';
    }
    
    if (!certificationData.issueDate) {
      errors.issueDate = 'Issue date is required';
    }
    
    if (certificationData.expiryDate && new Date(certificationData.issueDate) >= new Date(certificationData.expiryDate)) {
      errors.expiryDate = 'Expiry date must be after issue date';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Subscribe to education events
  subscribe(event, callback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(callback);
  }

  // Unsubscribe from education events
  unsubscribe(event, callback) {
    if (this.subscribers.has(event)) {
      const callbacks = this.subscribers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Notify subscribers
  notifySubscribers(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${event}:`, error);
        }
      });
    }
  }

  // Save methods
  async saveDegrees() {
    try {
      await AsyncStorage.setItem('user_degrees', JSON.stringify(this.educationData.degrees));
    } catch (error) {
      console.error('❌ Error saving degrees:', error);
    }
  }

  async saveCertifications() {
    try {
      await AsyncStorage.setItem('user_certifications', JSON.stringify(this.educationData.certifications));
    } catch (error) {
      console.error('❌ Error saving certifications:', error);
    }
  }

  async saveCourses() {
    try {
      await AsyncStorage.setItem('user_courses', JSON.stringify(this.educationData.courses));
    } catch (error) {
      console.error('❌ Error saving courses:', error);
    }
  }

  async saveAchievements() {
    try {
      await AsyncStorage.setItem('user_achievements', JSON.stringify(this.educationData.achievements));
    } catch (error) {
      console.error('❌ Error saving achievements:', error);
    }
  }

  // Cleanup
  cleanup() {
    this.subscribers.clear();
    this.offlineQueue = [];
    this.isInitialized = false;
  }
}

export default EducationManagementService;

